import { Box, Skeleton, Stack, Group } from '@mantine/core';
import { forwardRef } from 'react';

export type SkeletonPreset =
  | 'card'
  | 'list-item'
  | 'avatar-text'
  | 'paragraph'
  | 'image-card'
  | 'table-row'
  | 'form-field'
  | 'stat-card';

export interface SkeletonGroupProps {
  preset?: SkeletonPreset;
  count?: number;
  animate?: boolean;
  gap?: number | string;
}

const SkeletonGroup = forwardRef<HTMLDivElement, SkeletonGroupProps>(
  (
    {
      preset = 'card',
      count = 1,
      animate = true,
      gap = 'md',
    },
    ref
  ) => {
    const renderSkeleton = (index: number) => {
      switch (preset) {
        case 'card':
          return (
            <Box key={index} p="md">
              <Skeleton height={160} mb="sm" animate={animate} />
              <Skeleton height={16} mb="xs" animate={animate} />
              <Skeleton height={12} width="70%" animate={animate} />
            </Box>
          );

        case 'list-item':
          return (
            <Group key={index} wrap="nowrap" p="sm">
              <Skeleton circle height={40} animate={animate} />
              <Box style={{ flex: 1 }}>
                <Skeleton height={14} mb={6} animate={animate} />
                <Skeleton height={10} width="60%" animate={animate} />
              </Box>
            </Group>
          );

        case 'avatar-text':
          return (
            <Group key={index} wrap="nowrap">
              <Skeleton circle height={48} animate={animate} />
              <Box style={{ flex: 1 }}>
                <Skeleton height={16} width="40%" mb={8} animate={animate} />
                <Skeleton height={12} width="80%" animate={animate} />
              </Box>
            </Group>
          );

        case 'paragraph':
          return (
            <Stack key={index} gap={8}>
              <Skeleton height={12} animate={animate} />
              <Skeleton height={12} animate={animate} />
              <Skeleton height={12} width="80%" animate={animate} />
              <Skeleton height={12} width="60%" animate={animate} />
            </Stack>
          );

        case 'image-card':
          return (
            <Box key={index}>
              <Skeleton height={200} mb="md" animate={animate} />
              <Group mb="sm">
                <Skeleton circle height={32} animate={animate} />
                <Box style={{ flex: 1 }}>
                  <Skeleton height={14} width="50%" mb={4} animate={animate} />
                  <Skeleton height={10} width="30%" animate={animate} />
                </Box>
              </Group>
              <Skeleton height={12} mb={6} animate={animate} />
              <Skeleton height={12} width="90%" animate={animate} />
            </Box>
          );

        case 'table-row':
          return (
            <Group key={index} wrap="nowrap" py="sm">
              <Skeleton height={14} width={60} animate={animate} />
              <Skeleton height={14} style={{ flex: 1 }} animate={animate} />
              <Skeleton height={14} width={80} animate={animate} />
              <Skeleton height={14} width={100} animate={animate} />
              <Skeleton height={24} width={60} animate={animate} />
            </Group>
          );

        case 'form-field':
          return (
            <Box key={index}>
              <Skeleton height={14} width={100} mb={6} animate={animate} />
              <Skeleton height={36} animate={animate} />
            </Box>
          );

        case 'stat-card':
          return (
            <Box key={index} p="md">
              <Group justify="space-between" mb="md">
                <Skeleton height={12} width={80} animate={animate} />
                <Skeleton circle height={32} animate={animate} />
              </Group>
              <Skeleton height={28} width={100} mb={8} animate={animate} />
              <Skeleton height={10} width="60%" animate={animate} />
            </Box>
          );

        default:
          return <Skeleton key={index} height={40} animate={animate} />;
      }
    };

    return (
      <Stack ref={ref} gap={gap}>
        {Array.from({ length: count }, (_, i) => renderSkeleton(i))}
      </Stack>
    );
  }
);

SkeletonGroup.displayName = 'SkeletonGroup';

export default SkeletonGroup;
